/*
  Barre Cacao: storefront behaviour. Vanilla JS, no dependencies.
  Loaded with `defer` after Dawn's global.js / pubsub.js / cart-drawer.js.

  1. <bc-buy-form>: AJAX add-to-cart → Dawn cart drawer, qty stepper, price × qty
  2. Videos: lazy sources, play only in view, pause/play toggle, reduced motion
  3. Scroll reveal (IntersectionObserver)
  4. Sticky mobile buy bar
  5. Announcement dismiss, same-page anchors, mobile menu
  6. "When to eat it" carousel buttons, product gallery thumbs, marquee pause
*/
(() => {
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = () => reduceMotionQuery.matches;

  /* ── 1. Buy form ──────────────────────────────────────────────── */
  class BcBuyForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.submitButton = this.form.querySelector('button[name="add"]');
      this.errorEl = this.querySelector('.bc-buy__error');
      this.qtyInput = this.form.querySelector('.bc-qty__input');
      this.totalEl = this.querySelector('[data-bc-total]');
      this.qtyOut = this.querySelector('[data-bc-qty-out]');

      const totalsEl = this.querySelector('[data-bc-totals]');
      try {
        this.totals = totalsEl ? JSON.parse(totalsEl.textContent) : null;
      } catch (e) {
        this.totals = null;
      }

      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      if (this.qtyInput) {
        this.querySelectorAll('[data-bc-qty]').forEach((button) =>
          button.addEventListener('click', () => this.setQty(this.qty + Number(button.dataset.bcQty)))
        );
        this.qtyInput.addEventListener('change', () => this.setQty(this.qty));
        this.setQty(this.qty);
      }
    }

    get qty() {
      return parseInt(this.qtyInput?.value, 10) || 1;
    }

    get maxQty() {
      return parseInt(this.qtyInput?.max, 10) || 99;
    }

    setQty(value) {
      const qty = Math.min(Math.max(1, value), this.maxQty);
      this.qtyInput.value = qty;
      this.querySelectorAll('[data-bc-qty]').forEach((button) => {
        const step = Number(button.dataset.bcQty);
        button.disabled = (step < 0 && qty <= 1) || (step > 0 && qty >= this.maxQty);
      });
      if (this.qtyOut) this.qtyOut.textContent = qty;
      if (this.totalEl && this.totals && this.totals[qty - 1]) this.totalEl.textContent = this.totals[qty - 1];
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.submitButton;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      this.showError('');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      const drawer = document.querySelector('cart-drawer');
      const formData = new FormData(this.form);
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          drawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        drawer.setActiveElement(button);
      }

      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      const linesDeferred = this.dispatchLinesUpdate(variantId, quantity);
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();

        if (data.status) {
          const message = data.description || data.message || (window.cartStrings && window.cartStrings.error);
          this.showError(message);
          linesDeferred?.reject(new Error(message));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'bc-buy-form', productVariantId: variantId, errors: data.errors || data.description, message: data.message });
          }
          return;
        }

        this.resolveLinesUpdate(linesDeferred);

        if (!drawer) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bc-buy-form', productVariantId: variantId, cartData: data });
        }
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } catch (error) {
        console.error(error);
        this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
        linesDeferred?.reject(error);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }

    /* Mirrors Dawn's product-form.js so Shopify standard events keep firing. */
    dispatchLinesUpdate(variantId, quantity) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity }],
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    resolveLinesUpdate(deferred) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!deferred || !CartLinesUpdateEvent) return;
      fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.json`)
        .then((response) => response.json())
        .then((cart) => deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) }))
        .catch((error) => deferred.reject(error));
    }
  }
  if (!customElements.get('bc-buy-form')) customElements.define('bc-buy-form', BcBuyForm);

  /* ── 2. Videos ────────────────────────────────────────────────── */
  const userPaused = new WeakSet();

  const loadVideo = (video) => {
    if (!video.hasAttribute('data-bc-lazy')) return;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.removeAttribute('data-bc-lazy');
    video.load();
  };

  const syncToggle = (video) => {
    const toggle = video.id && document.querySelector(`[data-bc-video-toggle][aria-controls="${video.id}"]`);
    if (!toggle) return;
    const paused = video.paused;
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    const label = toggle.querySelector('[data-bc-toggle-label]');
    if (label) label.textContent = paused ? 'Play background video' : 'Pause background video';
  };

  const tryPlay = (video) => {
    if (prefersReducedMotion() || userPaused.has(video)) return;
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => syncToggle(video));
  };

  const videoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                loadVideo(video);
                tryPlay(video);
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const initVideos = (root = document) => {
    root.querySelectorAll('video[data-bc-video]').forEach((video) => {
      if (video.dataset.bcBound) return;
      video.dataset.bcBound = 'true';
      video.addEventListener('play', () => syncToggle(video));
      video.addEventListener('pause', () => syncToggle(video));
      if (prefersReducedMotion()) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      if (videoObserver) {
        videoObserver.observe(video);
      } else {
        loadVideo(video);
        tryPlay(video);
      }
      syncToggle(video);
    });

    root.querySelectorAll('[data-bc-video-toggle]').forEach((toggle) => {
      if (toggle.dataset.bcBound) return;
      toggle.dataset.bcBound = 'true';
      toggle.addEventListener('click', () => {
        const video = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!video) return;
        if (video.paused) {
          userPaused.delete(video);
          loadVideo(video);
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        } else {
          userPaused.add(video);
          video.pause();
        }
      });
    });
  };

  reduceMotionQuery.addEventListener?.('change', () => {
    document.querySelectorAll('video[data-bc-video]').forEach((video) => {
      if (prefersReducedMotion()) video.pause();
    });
  });

  /* ── 3. Scroll reveal ─────────────────────────────────────────── */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  const initReveal = (root = document) => {
    const items = root.querySelectorAll('[data-bc-reveal]:not(.is-in)');
    if (!revealObserver || !document.documentElement.classList.contains('bc-reveal-ready')) {
      items.forEach((item) => item.classList.add('is-in'));
      return;
    }
    items.forEach((item) => {
      const siblings = item.parentElement ? [...item.parentElement.children].filter((el) => el.hasAttribute('data-bc-reveal')) : [];
      item.style.setProperty('--bc-reveal-i', Math.min(siblings.indexOf(item), 5));
      revealObserver.observe(item);
    });
  };

  /* ── 4. Sticky mobile buy bar ─────────────────────────────────── */
  const initSticky = () => {
    const bar = document.querySelector('[data-bc-sticky]');
    if (!bar || bar.dataset.bcBound || !('IntersectionObserver' in window)) return;
    bar.dataset.bcBound = 'true';

    const start = document.querySelector('[data-bc-sticky-start]');
    const hideZones = [...document.querySelectorAll('[data-bc-sticky-hide]')];
    let pastStart = !start;
    const visibleZones = new Set();

    const update = () => {
      const show = pastStart && visibleZones.size === 0;
      bar.hidden = false;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle('bc-sticky-on', show);
    };

    if (start) {
      new IntersectionObserver(([entry]) => {
        pastStart = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(start);
    }
    if (hideZones.length) {
      const zoneObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? visibleZones.add(entry.target) : visibleZones.delete(entry.target)));
        update();
      });
      hideZones.forEach((zone) => zoneObserver.observe(zone));
    }
    update();
  };

  /* ── 5. Announcement, anchors, menu ───────────────────────────── */
  const initAnnouncement = () => {
    document.querySelectorAll('[data-bc-announce-close]').forEach((button) => {
      if (button.dataset.bcBound) return;
      button.dataset.bcBound = 'true';
      button.addEventListener('click', () => {
        const bar = button.closest('[data-bc-announce]');
        try {
          localStorage.setItem('bc-announce', bar.dataset.bcAnnounce);
        } catch (e) {}
        document.documentElement.classList.add('bc-announce-hidden');
        document.querySelector('#MainContent')?.focus({ preventScroll: true });
      });
    });
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-bc-anchor]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || !url.hash) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();

    const menu = link.closest('details[data-bc-menu]');
    if (menu) menu.removeAttribute('open');

    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.pushState(null, '', url.hash);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    const openMenu = document.querySelector('details[data-bc-menu][open]');
    if (!openMenu) return;
    openMenu.removeAttribute('open');
    openMenu.querySelector('summary')?.focus();
  });

  document.addEventListener('click', (event) => {
    const openMenu = document.querySelector('details[data-bc-menu][open]');
    if (openMenu && !openMenu.contains(event.target)) openMenu.removeAttribute('open');
  });

  /* ── 6. Carousel, gallery, marquee ────────────────────────────── */
  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-bc-track]').forEach((track) => {
      if (track.dataset.bcBound) return;
      track.dataset.bcBound = 'true';
      const buttons = document.querySelectorAll(`[data-bc-scroll][aria-controls="${track.id}"]`);
      const step = () => {
        const card = track.querySelector('li');
        const gap = parseFloat(getComputedStyle(track.querySelector('ul')).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      };
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        buttons.forEach((button) => {
          const dir = Number(button.dataset.bcScroll);
          button.disabled = dir < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max;
        });
      };
      buttons.forEach((button) =>
        button.addEventListener('click', () => {
          track.scrollBy({ left: step() * Number(button.dataset.bcScroll), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        })
      );
      track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  };

  const initGalleries = (root = document) => {
    root.querySelectorAll('[data-bc-gallery]').forEach((gallery) => {
      if (gallery.dataset.bcBound) return;
      gallery.dataset.bcBound = 'true';
      const track = gallery.querySelector('[data-bc-gallery-track]');
      const thumbs = [...gallery.querySelectorAll('[data-bc-thumb]')];
      const setCurrent = (id) =>
        thumbs.forEach((thumb) => thumb.setAttribute('aria-current', thumb.dataset.bcThumb === id ? 'true' : 'false'));

      thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => {
          const slide = document.getElementById(thumb.dataset.bcThumb);
          if (!slide) return;
          const left = track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
          track.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          setCurrent(slide.id);
        })
      );

      if ('IntersectionObserver' in window) {
        const slideObserver = new IntersectionObserver(
          (entries) => entries.forEach((entry) => entry.isIntersecting && setCurrent(entry.target.id)),
          { root: track, threshold: 0.6 }
        );
        gallery.querySelectorAll('[data-bc-slide]').forEach((slide) => slideObserver.observe(slide));
      }
    });
  };

  const initMarquee = (root = document) => {
    root.querySelectorAll('.bc-proof').forEach((strip) => {
      if (strip.dataset.bcBound) return;
      strip.dataset.bcBound = 'true';
      strip.addEventListener('click', () => strip.classList.toggle('is-paused'));
    });
  };

  /* ── Boot + theme editor ──────────────────────────────────────── */
  const init = (root = document) => {
    initVideos(root);
    initReveal(root);
    initCarousels(root);
    initGalleries(root);
    initMarquee(root);
    initAnnouncement();
    initSticky();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
